import React, { useState, ChangeEvent } from 'react';
import {
  Container,
  Typography,
  TextField,
  Button,
  Box,
  Alert,
  Grid,
} from '@mui/material';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import PhoneIcon from '@mui/icons-material/Phone';
import EmailIcon from '@mui/icons-material/Email';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import { useLanguage } from '../../contexts/LanguageContext';
import type { ContactFormData } from '../../types/api.types';
import { saveContact } from '../../services/storage';
import SEOHead from '../../components/SEOHead';

const CONTACT_INFO = [
  {
    icon: <LocationOnIcon />,
    label: 'Adresse',
    value: 'Stuttgartstraße 15-17, 44143 Dortmund',
    href: 'https://maps.google.com/?q=Stuttgartstraße+15-17,+44143+Dortmund',
  },
  {
    icon: <PhoneIcon />,
    label: 'Telefon',
    value: '+49 1575 5659651',
    href: 'tel:+4915755659651',
  },
  {
    icon: <EmailIcon />,
    label: 'E-Mail',
    value: 'info@top-bhs.de',
    href: 'mailto:info@top-bhs.de',
  },
  {
    icon: <WhatsAppIcon />,
    label: 'WhatsApp',
    value: '+49 1575 5659651',
    href: 'https://wa.me/4915755659651',
  },
];

const ContactPage: React.FC = () => {
  const { t } = useLanguage();
  const [formData, setFormData] = useState<ContactFormData>({
    name: '',
    email: '',
    phone: '',
    company: '',
    message: '',
  });
  const [success, setSuccess] = useState<boolean>(false);
  const [error, setError] = useState<string>('');

  const handleChange = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    try {
      saveContact(formData);
      setSuccess(true);
      setFormData({ name: '', email: '', phone: '', company: '', message: '' });
    } catch {
      setError(t('contact.error'));
    }
  };

  return (
    <Box>
      <SEOHead
        title={t('seo.contact_title')}
        description={t('seo.contact_description')}
        canonicalPath="/contact"
      />
      {/* Page Header */}
      <Box sx={{ bgcolor: '#f8f8f8', borderBottom: '1px solid #e8e8e8' }}>
        <Container maxWidth="lg" sx={{ pt: 16, pb: 10 }}>
          <Typography
            variant="overline"
            sx={{ color: 'text.secondary', letterSpacing: '3px', mb: 2, display: 'block' }}
          >
            {t('contact.subtitle')}
          </Typography>
          <Typography variant="h1" sx={{ mb: 3, fontSize: { xs: '2.5rem', md: '3.5rem' } }}>
            {t('contact.title')}
          </Typography>
          <Typography
            variant="body1"
            sx={{ color: 'text.secondary', maxWidth: 520, fontSize: '1.1rem', lineHeight: 1.75 }}
          >
            {t('contact.description')}
          </Typography>
        </Container>
      </Box>

      {/* Map */}
      <Box
        sx={{
          width: '100%',
          height: { xs: 220, sm: 300, md: 380 },
          borderTop: '1px solid #e8e8e8',
          borderBottom: '1px solid #e8e8e8',
        }}
      >
        <iframe
          title="TOP BHS Standort"
          src="https://maps.google.com/maps?q=Stuttgartstraße+15-17,+44143+Dortmund&output=embed"
          width="100%"
          height="100%"
          style={{ border: 0, display: 'block' }}
          loading="lazy"
          referrerPolicy="no-referrer-when-downgrade"
        />
      </Box>

      {/* Contact Info + Form */}
      <Container maxWidth="lg" sx={{ py: 10 }}>
        <Grid container spacing={6}>

          {/* Left: Contact Info */}
          <Grid item xs={12} md={4}>
            <Typography variant="h5" sx={{ fontWeight: 700, mb: 4 }}>
              Kontakt Info
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              {CONTACT_INFO.map((item) => (
                <Box
                  key={item.label}
                  component="a"
                  href={item.href}
                  target={item.href.startsWith('http') ? '_blank' : undefined}
                  rel={item.href.startsWith('http') ? 'noopener noreferrer' : undefined}
                  sx={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 2,
                    textDecoration: 'none',
                    color: 'inherit',
                    p: 2.5,
                    border: '1px solid #e8e8e8',
                    borderRadius: 2,
                    transition: 'border-color 0.2s, box-shadow 0.2s',
                    '&:hover': {
                      borderColor: '#1737bc',
                      boxShadow: '0 4px 16px rgba(23,55,188,0.08)',
                    },
                  }}
                >
                  <Box
                    sx={{
                      p: 1.2,
                      bgcolor: '#eef1fb',
                      borderRadius: 1.5,
                      color: '#1737bc',
                      display: 'flex',
                      alignItems: 'center',
                      flexShrink: 0,
                    }}
                  >
                    {item.icon}
                  </Box>
                  <Box>
                    <Typography
                      variant="caption"
                      sx={{ color: 'text.secondary', display: 'block', mb: 0.3, letterSpacing: '0.5px' }}
                    >
                      {item.label}
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {item.value}
                    </Typography>
                  </Box>
                </Box>
              ))}
            </Box>
          </Grid>

          {/* Right: Contact Form */}
          <Grid item xs={12} md={8}>
            <Typography variant="h5" sx={{ fontWeight: 700, mb: 4 }}>
              Nachricht senden
            </Typography>

            {success && (
              <Alert severity="success" sx={{ mb: 3 }}>
                {t('contact.success')}
              </Alert>
            )}
            {error && (
              <Alert severity="error" sx={{ mb: 3 }}>
                {error}
              </Alert>
            )}

            <Box component="form" onSubmit={handleSubmit}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label={t('contact.name')}
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label={t('contact.email')}
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label={t('contact.phone')}
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label={t('contact.company')}
                    name="company"
                    value={formData.company}
                    onChange={handleChange}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label={t('contact.message')}
                    name="message"
                    multiline
                    rows={6}
                    value={formData.message}
                    onChange={handleChange}
                    required
                  />
                </Grid>
                <Grid item xs={12}>
                  <Button type="submit" variant="contained" size="large" fullWidth>
                    {t('contact.send')}
                  </Button>
                </Grid>
              </Grid>
            </Box>
          </Grid>

        </Grid>
      </Container>
    </Box>
  );
};

export default ContactPage;
